import { DEFAULT_NEC_EDITION, type NecEdition } from '../tables/necEditions';

export type { NecEdition } from '../tables/necEditions';

// NEC Edition Information. Every table and citation in the engine is written
// to this edition; the user-selectable edition only renumbers citations.
export const NEC_EDITION = {
  version: DEFAULT_NEC_EDITION,
  standard: `NFPA 70-${DEFAULT_NEC_EDITION}`,
  description: 'National Electrical Code',
} as const;

// Wire size types - AWG as negative, kcmil as positive
// #14 = -14, #1 = -1, 1/0 = 0, 2/0 = 0.5 (special), 3/0 = 0.75, 4/0 = 0.9
// 250 kcmil = 250, 500 kcmil = 500, etc.

export type WireSize =
  | -14 | -12 | -10 | -8 | -6 | -4 | -3 | -2 | -1
  | 0 | 0.5 | 0.75 | 0.9  // 1/0, 2/0, 3/0, 4/0
  | 250 | 300 | 350 | 400 | 500 | 600 | 700 | 750 | 800 | 900 | 1000 | 1250;

// Display names for wire sizes
export const WIRE_SIZE_DISPLAY: Record<number, string> = {
  [-14]: '#14',
  [-12]: '#12',
  [-10]: '#10',
  [-8]: '#8',
  [-6]: '#6',
  [-4]: '#4',
  [-3]: '#3',
  [-2]: '#2',
  [-1]: '#1',
  [0]: '#1/0',
  [0.5]: '#2/0',
  [0.75]: '#3/0',
  [0.9]: '#4/0',
  [250]: '250 KCMIL',
  [300]: '300 KCMIL',
  [350]: '350 KCMIL',
  [400]: '400 KCMIL',
  [500]: '500 KCMIL',
  [600]: '600 KCMIL',
  [700]: '700 KCMIL',
  [750]: '750 KCMIL',
  [800]: '800 KCMIL',
  [900]: '900 KCMIL',
  [1000]: '1000 KCMIL',
  [1250]: '1250 KCMIL',
};

// Ordered wire sizes from smallest to largest
export const WIRE_SIZES_ORDERED: WireSize[] = [
  -14, -12, -10, -8, -6, -4, -3, -2, -1,
  0, 0.5, 0.75, 0.9,
  250, 300, 350, 400, 500, 600, 700, 750, 800, 900, 1000, 1250
];

export type ConductorMaterial = 'copper' | 'aluminum';

// Temperature rating for conductors per NEC Table 310.16
export type TemperatureRating = 60 | 75 | 90;

export const TEMPERATURE_RATING_DISPLAY: Record<TemperatureRating, string> = {
  60: '60°C (TW, UF)',
  75: '75°C (THW, THWN, XHHW)',
  90: '90°C (THWN-2, XHHW-2)',
};

export type ConduitType = 'PVC40' | 'PVC80' | 'RMC' | 'EMT' | 'Aluminum';

export const CONDUIT_TYPE_DISPLAY: Record<ConduitType, string> = {
  'PVC40': 'Sch 40 PVC',
  'PVC80': 'Sch 80 PVC',
  'RMC': 'RMC',
  'EMT': 'EMT',
  'Aluminum': 'Aluminum',
};

export type Phase = 'single' | 'three';

export type Voltage = 120 | 208 | 240 | 277 | 480 | number;

export type UnitSystem = 'imperial' | 'metric';

export interface GlobalSettings {
  voltage: Voltage;
  phase: Phase;
  conductorMaterial: ConductorMaterial;
  temperatureRating: TemperatureRating;
  maxConductorSize: WireSize;
  conduitType: ConduitType;
  ambientTemperature: number; // Celsius
  conduitFillDerating: boolean;
  conduitOversizeEnabled: boolean;
  conduitOversizePercent: number;
  unitSystem: UnitSystem;
  /** NEC edition used for the citations shown and printed; never changes a result. */
  necEdition: NecEdition;
}

export const DEFAULT_SETTINGS: GlobalSettings = {
  voltage: 480,
  phase: 'three',
  conductorMaterial: 'copper',
  temperatureRating: 75,
  maxConductorSize: 600,
  conduitType: 'PVC40',
  ambientTemperature: 30,
  conduitFillDerating: false,
  conduitOversizeEnabled: true,
  conduitOversizePercent: 30,
  unitSystem: 'imperial',
  necEdition: DEFAULT_NEC_EDITION,
};

// Unit conversion utilities
export const UNIT_CONVERSIONS = {
  // Length
  ftToM: (ft: number) => ft * 0.3048,
  mToFt: (m: number) => m / 0.3048,
  inToMm: (inches: number) => inches * 25.4,
  mmToIn: (mm: number) => mm / 25.4,
  // Area
  sqInToSqMm: (sqIn: number) => sqIn * 645.16,
  sqMmToSqIn: (sqMm: number) => sqMm / 645.16,
  // Temperature
  cToF: (c: number) => (c * 9/5) + 32,
  fToC: (f: number) => (f - 32) * 5/9,
};

export const UNIT_LABELS: Record<UnitSystem, { distance: string; distanceShort: string; area: string; temperature: string }> = {
  imperial: {
    distance: 'feet',
    distanceShort: 'ft',
    area: 'in²',
    temperature: '°F',
  },
  metric: {
    distance: 'meters',
    distanceShort: 'm',
    area: 'mm²',
    temperature: '°C',
  },
};

// Company standards (hard-coded minimums)
export const COMPANY_STANDARDS: { minConductorSize: WireSize; minConduitSize: number } = {
  minConductorSize: -12, // #12 AWG
  minConduitSize: 1, // 1 inch
};

// Standard conduit sizes in inches
export const CONDUIT_SIZES = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6];

// NEC reference types
export interface NECReference {
  table: string;
  article?: string;
  description: string;
}

// Calculation result types
export interface AmpacityResult {
  baseAmpacity: number;
  baseAmpacity75C: number;
  baseAmpacity90C?: number;
  deratedAmpacity90C?: number;
  wasLimitedBy75C?: boolean;
  temperatureFactor: number;
  conduitFillFactor: number;
  finalAmpacity: number;
  references: NECReference[];
}

export interface ConduitSizingResult {
  conductorArea: number;
  minConduitSize: number;
  actualFillPercent: number;
  maxFillPercent: number;
  status: 'ok' | 'warning' | 'error';
  /** False when no available size of the chosen raceway can hold the conductors;
   *  minConduitSize is then the largest available size and status is 'error'. */
  fits: boolean;
  references: NECReference[];
}

export interface VoltageDropResult {
  voltageDrop: number;
  voltageDropPercent: number;
  voltageAtLoad: number;
  status: 'ok' | 'warning' | 'error';
  references: NECReference[];
  calculationMethod?: VoltageDropMethod;
}

export interface ConduitWireSpec {
  conduitQty: number;
  conduitSize: number;
  conduitType: ConduitType;
  phaseQty: number;
  phaseSize: WireSize;
  neutralSize?: WireSize;
  groundSize: WireSize;
}

// Format conduit and wire spec to string
export function formatConduitWireSpec(spec: ConduitWireSpec): string {
  const conduitDisplay = CONDUIT_TYPE_DISPLAY[spec.conduitType];
  const phaseDisplay = WIRE_SIZE_DISPLAY[spec.phaseSize] || `${spec.phaseSize}`;
  const groundDisplay = WIRE_SIZE_DISPLAY[spec.groundSize] || `${spec.groundSize}`;

  let result = `(${spec.conduitQty}) ${spec.conduitSize}" ${conduitDisplay} W/${spec.phaseQty}-${phaseDisplay}`;

  if (spec.neutralSize !== undefined) {
    const neutralDisplay = WIRE_SIZE_DISPLAY[spec.neutralSize] || `${spec.neutralSize}`;
    result += ` + ${neutralDisplay} NEU`;
  }

  result += ` + ${groundDisplay} GND`;

  return result;
}

// Motor starter types
// Voltage drop calculation method
export type VoltageDropMethod = 'ieee' | 'conservative';

export const VOLTAGE_DROP_METHOD_DISPLAY: Record<VoltageDropMethod, string> = {
  ieee: 'IEEE Method (NEC Table 9)',
  conservative: 'Conservative Method (NEC Table 8)',
};

export type StarterType = 'fvnr' | 'soft-start' | 'vfd';

export const STARTER_TYPE_DISPLAY: Record<StarterType, string> = {
  fvnr: 'Across-the-Line (FVNR)',
  'soft-start': 'Soft Starter',
  vfd: 'VFD',
};

// Feeder circuit result for stand-alone starters
export interface FeederCircuitResult {
  currentBasis: number;       // Current used for sizing (VFD input or motor FLA)
  requiredAmpacity: number;   // 125% of current basis
  breakerSize: number;        // Breaker at distribution panel (250% motor FLC per NEC 430.52)
  conductorSize: WireSize;
  groundSize: WireSize;
  conduitSize: string;
  parallelSets: number;
}

// Motor data types
export interface BGDMotorData {
  /** True when the row was derived from NEC rules rather than taken from the BGD source table. */
  derived?: boolean;
  hp: number;
  fla: number;
  wire: WireSize;
  ground: WireSize;
  conduit: string;
  starter: number;
  lra: number;
  mcp: string;
  sbkw: number;
  kva: number;
}

// Special cable types
export interface SpecialCable {
  name: string;
  outerDiameter: number; // inches
}

export const SPECIAL_CABLES: SpecialCable[] = [
  { name: '#18 Twisted Shielded Pair', outerDiameter: 0.274 },
  { name: '#16 Twisted Shielded Pair', outerDiameter: 0.324 },
  { name: 'Ethernet Cable', outerDiameter: 0.252 },        // 0.0500 in²
  { name: 'Sh. Eth. Cable', outerDiameter: 0.335 },        // 0.0881 in²
  { name: 'WP Eth Cable', outerDiameter: 0.295 },          // 0.0683 in²
  { name: 'WP Sh. Eth. Cable', outerDiameter: 0.335 },     // 0.0881 in²
];

// VFD Cables - Lutze with 3 Symmetrical Grounds (3 Power + 3 Protective Earth Grounds)
export const VFD_CABLES: SpecialCable[] = [
  { name: '3/C #6 + 3/C #12 Gnd - Lutze A2200603', outerDiameter: 0.941 },
  { name: '3/C #4 + 3/C #12 Gnd - Lutze A2200403', outerDiameter: 1.039 },
  { name: '3/C #2 + 3/C #10 Gnd - Lutze A2200203', outerDiameter: 1.155 },
  { name: '3/C #1 + 3/C #8 Gnd - Lutze A2200103', outerDiameter: 1.385 },
  { name: '3/C 1/0 + 3/C #8 Gnd - Lutze A2201/003', outerDiameter: 1.462 },
  { name: '3/C 2/0 + 3/C #8 Gnd - Lutze A2202/003', outerDiameter: 1.540 },
  { name: '3/C 3/0 + 3/C #6 Gnd - Lutze A2203/003', outerDiameter: 1.630 },
  { name: '3/C 4/0 + 3/C #6 Gnd - Lutze A2204/003', outerDiameter: 1.880 },
  { name: '3/C 250MCM + 3/C #6 Gnd - Lutze A22025003', outerDiameter: 2.032 },
  { name: '3/C 350MCM + 3/C #4 Gnd - Lutze A22035003', outerDiameter: 2.340 },
  { name: '3/C 500MCM + 3/C #4 Gnd - Lutze A22050003', outerDiameter: 2.589 },
];

// Calculation history entry
export interface CalculationHistoryEntry {
  id: string;
  timestamp: number;
  calculatorType: string;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  summary: string;
  name?: string;  // Optional user-defined name for the entry
}
